import axios from 'axios';
import PhoneInput, { formatPhoneNumber, formatPhoneNumberIntl, isPossiblePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input'
import { useDispatch, useSelector } from 'react-redux';
import { fetchAuthenticatedExistingPolicies } from './helper';
  
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const STARLIFE_API = process.env.REACT_APP_API_URL;



export async function getSecurityQuestions(signupToken) {
  if (!signupToken) {
    return {
      success: false,
      message: 'Signup token is missing. Please log in again.',
    };
  }

  try {
    const url = `${API_BASE_URL}/client/security-questions`; // Adjust the URL according to your API structure
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${signupToken}`,
      },
    });

    // Check if the response contains the security questions
    if (response.status === 200 && response.data.security_questions) {
      return {
        success: true,
        message: response.data.message,
        security_questions: response.data.security_questions, // Return the list of security questions
      };
    } else {
      // Handle cases where the structure is unexpected
      return {
        success: false,
        message: response.data.message || 'Failed to retrieve security questions.',
      };
    }
  } catch (error) {
    // Handle errors gracefully, checking if the error has a response message
    return {
      success: false,
      message: error.response?.data?.message || 'An error occurred while fetching security questions.',
    };
  }
}


export async function submitSecurityQuestions(signupToken, questions) {
  if (!signupToken) {
    return {
      success: false,
      message: 'Signup token is missing. Please log in again.',
    };
  }

  try {
    const url = `${API_BASE_URL}/client/security-questions`; // Adjust the endpoint as needed
    const payload = { security_questions: questions };

    const response = await axios.post(url, payload, {
      headers: {
        Authorization: `Bearer ${signupToken}`,
      },
    });

    // Check for a successful response
    if (response.status === 200 && response.data.message) {
      return {
        success: true,
        message: response.data.message,
        data:response.data
      };
    } else {
      // Handle unexpected response structure
      return {
        success: false,
        message: response.data.message || 'Failed to set security questions.',
      };
    }
  } catch (error) {
    // Handle errors gracefully
    return {
      success: false,
      message: error.response?.data?.message || 'An error occurred while submitting security questions.',
    };
  }
}

export async function register(values) {
  const payload = {
    phoneNumber: values.phoneNumber,
    password: values.password,
    userRole: "client",
  };

  try {
    const url = `${API_BASE_URL}/client/register`;
    const response = await axios.post(url, payload);

    if ([200, 201].includes(response.status)) {
      return {
        success: true,
        message: response.data.message,
        data: response.data,
      };
    }

    return {
      success: false,
      message: response.data.message,
    };

  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message,
      errorDetails: error.response?.data,
    };
  }
}




export async function agentRegister(values) {
  const payload = {
    agent_code: values.agentCode,
    password: values.password,
  };

  try {
    const url = `${API_BASE_URL}/agent/register`;
    const response = await axios.post(url, payload);

    if ([200, 201, 202].includes(response.status)) {
      return {
        success: true,
        message: response.data.message,
        data: response.data, // contains { message, user:{...} }
      };
    }

    return {
      success: false,
      message: response.data.message,
    };

  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message,
      errorDetails: error.response?.data,
    };
  }
}

export async function requestPasswordReset(phoneNumberOrAgentCode, userRole) {
  // Prepare the payload based on the userRole
  const payload = userRole === 'client' 
    ? { phoneNumber: phoneNumberOrAgentCode.replace(/^(\+233)/, '0') }
    : { agent_code: phoneNumberOrAgentCode }; // Use agentCode for agents

  try {
    // Adjust the endpoint based on the userRole
    const url = `${API_BASE_URL}/${userRole}/forgot-password`;

    // Make the request
    const response = await axios.post(url, payload);

    // Handle the response
    return response.status === 200
      ? { success: true, message: response.data.message }
      : { success: false, message: response.data.message || 'Failed to send password reset email.' };

  } catch (error) {
    // Handle errors gracefully
    return { success: false, message: error.response?.data?.message || 'An error occurred while requesting password reset.' };
  }
}




export async function resetPassword(userRole, token, new_password, confirm_password, agentCode) {
  console.log("resetPassword called with the following parameters:");
  console.log("userRole:", userRole);
  console.log("tokenOrAgentCode:", token);
  console.log("new_password:", new_password);
  console.log("confirm_password:", confirm_password);
  console.log("agentCode:", agentCode);

  const payload = {
    new_password,
    confirm_password,
  };

  // Add the agent_code to the payload if userRole is 'agent'
  if (userRole === 'agent') {
    payload.agent_code = agentCode; // Use agent_code for agent role
  }

  console.log("Constructed Payload:", payload);

  try {
    // Determine the URL based on userRole
    const url = userRole === 'client'
      ? `${API_BASE_URL}/client/reset-password`
      : `${API_BASE_URL}/agent/reset-password`;

    // Prepare headers
    const headers = userRole === 'client'
      ? { Authorization: `Bearer ${token}` }  // Include token for client
      : {};  // No token for agent

    const response = await axios.post(url, payload, { headers });

    if (response.status === 200 && response.data.message) {
      return {
        success: true,
        message: response.data.message || 'Password reset successful.',
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Failed to reset password.',
      };
    }
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || 'An error occurred during password reset.',
    };
  }
}



export async function verifyResetOTP(agentCode, otp) {
  try {
    const payload = {
      agent_code: agentCode,
      otp: otp
    };

    const url = `${API_BASE_URL}/agent/verify-reset-otp`;
    const response = await axios.post(url, payload);

    if (response.status === 200) {
      return {
        success: true,
        message: response.data.message,
        verified: response.data.verified,
        agent_code: response.data.agent_code
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Failed to verify OTP'
      };
    }
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || 'An error occurred while verifying OTP'
    };
  }
}


/** login request */
export async function login(values) {
  const isAgent = values.userRole === 'agent';
  const payload = isAgent
    ? {
        agent_code: values.agentCode,
        password: values.password,
        userRole: 'agent',
      }
    : {
        phoneNumber: values.phoneNumber,
        password: values.password,
        userRole: 'client',
      };

  try {
    const url = `${API_BASE_URL}/${isAgent ? 'agent' : 'client'}/login`;
    const response = await axios.post(url, payload);

    if (response.status === 200 && response.data.access_token) {
      return {
        success: true,
        message: response.data.message || 'Login successful',
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
        user: response.data.user, 
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'An error occurred during login',
      };
    }
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
}


export async function checkUserPolicies(phoneNumber, accessToken) {
  try {
    const response = await fetchAuthenticatedExistingPolicies(phoneNumber, { accessToken });
    const policies = response?.policies || [];

    if (policies.length > 0) {
      return {
        success: true,
        policies,
      };
    } else {
      return {
        success: false,
        message: 'No policies found or an error occurred.',
      };
    }
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
}


// Verify Account function
export async function verifyEmail(token, otp, user_type) {
  try {
    // Normalize the user_type to lowercase for consistent comparison
    const normalizedUserType = (user_type || '').toLowerCase().trim();
    
    // Define all possible agent-related roles
    const agentRoles = ['agent', 'telesales', 'team-leader', 'supervisor', 'admin'];
    
    // Check if the user_type is an agent-related role
    const isAgentType = agentRoles.includes(normalizedUserType);
    
    // Determine the correct endpoint
    const endpoint = isAgentType ? 'agent' : 'client';
    const url = `${API_BASE_URL}/${endpoint}/verify-account`;

    // Prepare the payload with token and otp
    const payload = {
      otp: otp,
    };

    // Log for debugging (optional - remove in production)
    console.log(`Verifying ${endpoint} account with user_type: ${user_type}`);

    // Make the POST request with the payload and Authorization header
    const response = await axios.post(url, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    // Check for expected fields in the response
    if (response.data && (response.data.user || response.data.success)) {
      // Successfully handled response with expected structure
      return {
        success: true,
        message: response.data.message || 'Verification successful',
        data: response.data,
      };
    } else {
      // Gracefully handle unexpected response structure
      return {
        success: false,
        message: response.data?.message || 'Verification failed.',
      };
    }
  } catch (error) {
    // Improved error handling for network or unexpected errors
    const errorMessage = error.response?.data?.message || 
                        error.response?.data?.error || 
                        error.message || 
                        'An error occurred during verification.';
    
    // Log the error for debugging
    console.error('Verification error:', errorMessage);
    
    return {
      success: false,
      message: errorMessage,
    };
  }
}



export async function verifyPhoneNumber(otp, signupToken, user_type) {
  try {
    // Set up an Axios request interceptor to log headers (optional, for debugging)
    // Note: Be careful with interceptors as they affect ALL requests
    // Consider removing or using a more targeted approach in production
    const originalRequestInterceptor = axios.interceptors.request.use(
      (config) => {
        if (config.url.includes('/verify-account')) {
          console.log('Verification Request Headers:', config.headers);
          console.log('Verification Request Payload:', config.data);
        }
        return config;
      },
      (error) => {
        console.error('Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Normalize user_type for consistent comparison
    const normalizedUserType = (user_type || '').toLowerCase().trim();
    
    // Define all agent-related roles
    const agentRoles = ['agent', 'telesales', 'team-leader', 'supervisor', 'admin'];
    
    // Determine if user is an agent type
    const isAgentType = agentRoles.includes(normalizedUserType);
    
    // Construct the URL
    const endpoint = isAgentType ? 'agent' : 'client';
    const url = `${API_BASE_URL}/${endpoint}/verify-account`;
    
    console.log(`Verifying phone for ${endpoint} with user_type: ${user_type}`);

    // Make the API request with OTP
    const response = await axios.post(
      url,
      { otp },
      {
        headers: {
          Authorization: `Bearer ${signupToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    // Clean up the interceptor to avoid memory leaks
    axios.interceptors.request.eject(originalRequestInterceptor);

    // Check for successful response
    if (response.data && (response.data.user || response.data.success || response.data.token)) {
      return {
        success: true,
        message: response.data.message || 'Phone verification successful',
        data: response.data,
      };
    } else {
      console.log('Unexpected response structure:', response.data);
      return {
        success: false,
        message: response.data?.message || 'Verification failed.',
      };
    }
  } catch (error) {
    console.error('Error during phone verification:', error);
    
    // Extract meaningful error message
    let errorMessage = 'An error occurred during verification.';
    
    if (error.response) {
      // Server responded with error status
      errorMessage = error.response.data?.message || 
                    error.response.data?.error || 
                    error.response.statusText || 
                    `Server error: ${error.response.status}`;
    } else if (error.request) {
      // Request was made but no response received
      errorMessage = 'No response from server. Please check your network connection.';
    } else {
      // Something else happened
      errorMessage = error.message || 'Unknown error occurred.';
    }

    return {
      success: false,
      message: errorMessage,
      error: error.response?.data || null,
    };
  }
}



export const verifyAgentOtp = async (otp, agentCode) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/agent/verify-otp`, {
      otp,
      agent_code: agentCode
    });

    return {
      success: true,
      message: res.data.message
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || "Verification failed"
    };
  }
};





export async function issueLogout(accessToken) {
  if (!accessToken) {
    return {
      success: false,
      message: 'Access token is missing. Please log in again.',
      user: {}, 
    };
  }

  try {
    const url = `${API_BASE_URL}/client/logout`; 
    const response = await axios.post(url, null, {
      headers: {
        Authorization: `Bearer ${accessToken}`, // Pass the access token in the request headers
      },
    });

    // Check if the server successfully handled the logout request
    if (response.status === 200) {
      return {
        success: true,
        message: response.data.message,
        user: response.data.user || {}, 
      };
    } else {
      // Handle unexpected response status
      return {
        success: false,
        message: response.data.message,
        user: {}, 
      };
    }
  } catch (error) {
   
    return {
      success: false,
      message: error.response?.data?.message,
      user: {}, 
    };
  }
}


/** Fetch the authenticated user's profile from the backend. */
export async function getUserProfile(accessToken) {
  if (!accessToken) {
    return { success: false, message: 'Access token is missing. Please log in again.' };
  }

  try {
    const url = `${API_BASE_URL}/auth/profile`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return { success: true, profile: response.data };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || 'Unable to load profile details.',
    };
  }
}

/** Update the authenticated user's editable profile fields (email, phone, address). */
export async function updateUserProfile(accessToken, updates) {
  if (!accessToken) {
    return { success: false, message: 'Access token is missing. Please log in again.' };
  }

  try {
    const url = `${API_BASE_URL}/auth/profile`;
    const response = await axios.put(url, updates, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return { success: true, message: response.data?.message || 'Profile updated successfully.' };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || 'Unable to update profile. Please try again.',
      errors: error.response?.data?.errors,
    };
  }
}


/** Activate Account with OTP by retrieving the access token from local storage */
export async function activateAccount(values) {
  const agentData = JSON.parse(localStorage.getItem('agentData'));
  const agent_code = (agentData && agentData.agent_code) || localStorage.getItem('agentCode');
  
    const token = localStorage.getItem('agentData') ? JSON.parse(localStorage.getItem('agentData')).access_token : null;

 try {
    const payload = {
      otp: values.otp,
      agent_code: agent_code,
    };

    const url = `${API_BASE_URL}/activate-account`;
    const response = await axios.post(url, payload);
  

   if (response.status === 200) {
      return {
        success: true,
        message: response.data.message
      };
    } else {
      return {
        success: false,
        message: 'An error occurred while activating the account'
      };
    }
  } catch (error) {
    if (error.response) {
      const responseData = error.response.data;
      
      if (responseData.message === 'Both OTP and agent_code are required') {
        return {
          success: false,
          message: 'Both OTP and agent_code are required'
        };
      } else if (responseData.message === 'Sales executive not found') {
        return {
          success: false,
          message: 'Sales executive not found'
        };
      } else if (responseData.message === 'OTP has already been used') {
        return {
          success: false,
          message: 'OTP has already been used'
        };
      } else if (responseData.message === 'OTP has expired') {
        return {
          success: false,
          message: 'OTP has expired'
        };
      } else if (responseData.message === 'Invalid OTP') {
        return {
          success: false,
          message: 'Invalid OTP'
        };
      } else {
        return {
          success: false,
          message: 'An error occurred while activating the account'
        };
      }
    } else {
      return {
        success: false,
        message: 'An error occurred while making the request'
      };
    }
  }
}



export async function otpVerification(values) {
  const agentData = JSON.parse(localStorage.getItem('agentData'));
 const agentCode = agentData && agentData.agent_code ? agentData.agent_code : localStorage.getItem('agentCode');

   if (!agentCode) {
    return {
      success: false,
      message: 'Agent code is missing. Please log in again.',
    };
  }

 const payload = {
    otp: values.otp,
    agent_code: agentCode,
  };

  console.log('otp payload:', payload)


  try {
    const url = `${API_BASE_URL}/verify-otp`;
    const response = await axios.post(url, payload);
    console.log('response:', response);
    const responseData = response.data;

    if (responseData && responseData.message === "OTP verification successful") {
      return {
        success: true,
        message: responseData.message,
      };
    } else {
      return {
        success: false,
        message: responseData.message,
      };
    }
  } catch (error) {
    const message = error.response?.data?.message || 'An error occurred during OTP verification. Please try again.';
    return {
      success: false,
      message,
    };
  }
}

export async function clientOtpGenerate(clientPhoneNumber, productInitials) {
  const payload = {
    phone_number: clientPhoneNumber,
    product: productInitials
  };

  try {
    const url = `${API_BASE_URL}/client/generate-otp`;
    const response = await axios.post(url, payload);

    if (response.status === 201) {
      return {
        success: true,
        otpCode: response.data.data?.otp_code
      };
    } else if (response.status === 200) {
      return {
        success: true,
        message: response.data.message 
      };
    } else {
      return {
        success: false,
        message: "Unexpected response from server."
      };
    }
  } catch (error) {
    if (error.response && error.response.data && error.response.data.message) {
      return {
        success: false,
        message: error.response.data.message
      };
    } else {
      return {
        success: false,
        message: "An error occurred while generating OTP. Please try again."
      };
    }
  }
}



let applicationID = localStorage.getItem('currentApplicationID');

  const ongoingApplications = JSON.parse(localStorage.getItem('ongoingApplications')) || [];
  const matchingApplication = ongoingApplications.find((application) => application.id === applicationID);





export async function clientOtpVerify(values, productInitials) {
  const payload = {
    otp: values.otp,
    product:productInitials
  };
  try {
    const url = `${API_BASE_URL}/client/verify-otp`;
    const response = await axios.post(url, payload);
    const responseData = response.data;

    if (responseData && responseData.message === "OTP verification successful") {
      return {
        success: true,
        message: responseData.message
      };
    } else {
      return {
        success: false,
        message: responseData.message
      };
    }
  } catch (error) {
    if (error.response && error.response.data && error.response.data.message) {
      return {
        success: false,
        message: error.response.data.message
      };
    } else {
      return {
        success: false,
        message: 'An error occurred during OTP verification. Please try again.'
      };
    }
  }
}



/** Reset password */


export async function passwordReset(values) {
  const agent_code = ((localStorage.getItem('agentData') ? JSON.parse(localStorage.getItem('agentData')) : null)?.agent_code) || localStorage.getItem('agentCode');


  const payload = {
    password: values.password,
    agent_code: agent_code,
    confirm_password: values.confirm_password,
  };

  

  try {
    const url = `${API_BASE_URL}/password_reset`;
    const response = await axios.post(url, payload);
    const responseData = response.data;

    if (response.status === 200) {
      return {
        success: true,
        message: responseData.message,
      };
    } else {
      return {
        success: false,
        message: responseData.message,
      };
    }
  } catch (error) {
    if (error.response) {
      const responseData = error.response.data;
      return {
        success: false,
        message: responseData.message,
      };
    } else {
      return {
        success: false,
        message: 'An error occurred during password reset. Please try again.',
      };
    }
  }
}



/** send OTP */

export async function sendOTP(agent_code) {
  try {
    const url = `${API_BASE_URL}/agent/generate-otp`;
    const response = await axios.post(url, { agent_code: agent_code });

    return {
      success: true,
      message: response.data?.message || 'If the account is valid, a verification code has been sent.',
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || 'An error occurred while sending OTP. Please try again.',
    };
  }
}



